import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import {
  clearPersistedAuthSession,
  isAuthSessionStorageEvent,
  loadPersistedAuthSession,
  persistAuthSession,
  type AuthSession,
  type MembershipResolution,
} from "@/modules/auth/public";
import { disposeDatasetFactsEditors } from "@/modules/datasets/public";
import {
  closeRerunWindows,
  disposeInspectSessions,
  mappingChanges,
} from "@/modules/inspect/public";
import { clearJWT, onSessionInvalidated, toErrorLogContext } from "@/shared/http";
import { createLogger } from "@/shared/logging";
import { isPageHidden } from "@/shared/utils/pageVisibility";
import { windowStack } from "../shell/windowStack";
import { serverInfoController } from "../shell/serverInfoController";
import { appUpdateController } from "../shell/appUpdateController";
import {
  appStateReducer,
  createInitialAppState,
  initializeAppState,
  resolveRestoredAuthState,
  verifyAuthSession,
  type AppState,
  type InitializeAppStateOptions,
} from "./appState";

const logger = createLogger("app/state");

// 세션에 묶인 전역 리소스 — 전체화면 창은 조직 데이터를 들고 있어 세션이 끝나면 함께 닫는다(규칙 §8).
// 점검 세션 · 매핑 편집 · 조사 사실 편집도 잡은 상세와 관계없이 버린다. Rerun 새 창 뷰어도 닫는다. 뷰어 껍데기는
// 로그아웃을 감지하지 않는다.
// 닫기 훅 하나가 실패해도 로그아웃 자체는 막지 않는다.
function closeSessionWindows(): void {
  mappingChanges.stop();
  disposeInspectSessions();
  disposeDatasetFactsEditors();
  try {
    windowStack.clear();
  } catch (error: unknown) {
    logger.error("Failed to close windows at session end.", error);
  }
  try {
    closeRerunWindows();
  } catch (error: unknown) {
    logger.error("Failed to close Rerun viewer windows at session end.", error);
  }
}

type AppStateContextValue = {
  state: AppState;
  // 로그인 응답의 세션을 활성화한다 — 메모리 JWT 설치 → Intake `/v1/me` 로 Member 확인 → 저장 · 상태 전이.
  // `member` 가 아니면 세션을 만들지 않고 그 결과를 그대로 돌려준다(콜백 화면이 문구로 옮긴다).
  startSession: (session: AuthSession) => Promise<MembershipResolution>;
  endSession: () => void;
};

type AppStateProviderProps = InitializeAppStateOptions & {
  children: ReactNode;
  splash: ReactNode;
};

const AppStateContext = createContext<AppStateContextValue | null>(null);

export function AppStateProvider({
  children,
  splash,
  minimumSplashDurationMs,
}: AppStateProviderProps) {
  const [state, dispatch] = useReducer(appStateReducer, undefined, () => createInitialAppState());
  // 스플래시 표시 여부는 UI 상태로 두고 AppState에는 앱 데이터만 둔다.
  const [isInitialized, setIsInitialized] = useState(false);

  const token = state.auth.status === "signedIn" ? state.auth.session.accessToken : null;
  useLayoutEffect(() => {
    if (token !== null) {
      serverInfoController.init();
      appUpdateController.init();
    }
    return () => {
      serverInfoController.dispose();
      appUpdateController.dispose();
    };
  }, [token]);

  useLayoutEffect(() => {
    if (token !== null) mappingChanges.start(token);
    return () => mappingChanges.stop();
  }, [token]);

  useEffect(() => {
    const reconcile = () => {
      if (token !== null && !isPageHidden()) mappingChanges.reconcile();
    };
    document.addEventListener("visibilitychange", reconcile);
    window.addEventListener("focus", reconcile);
    return () => {
      document.removeEventListener("visibilitychange", reconcile);
      window.removeEventListener("focus", reconcile);
    };
  }, [token]);

  useEffect(() => {
    let disposed = false;
    setIsInitialized(false);

    void initializeAppState({ minimumSplashDurationMs })
      .then((initializedState) => {
        if (disposed) {
          return;
        }
        dispatch({ type: "app/initialized", state: initializedState });
        setIsInitialized(true);
      })
      .catch((error: unknown) => {
        if (disposed) {
          return;
        }
        // 복원의 두 단계(저장소 읽기 · `/v1/me` 확인)는 각자 실패를 삼키므로 여기 오는 건 예상 밖이다.
        // 로그아웃 상태로 화면을 연다.
        logger.error("Failed to initialize app state.", toErrorLogContext(error));
        clearJWT();
        dispatch({ type: "app/initialized", state: createInitialAppState() });
        setIsInitialized(true);
      });

    return () => {
      disposed = true;
    };
  }, [minimumSplashDurationMs]);

  // 다른 탭에서 로그인/로그아웃이 발생하면 이 탭에도 동일하게 반영한다. 로그인은 그 탭이 이미 Member 를
  // 확인했으니 먼저 열고, 이 탭의 조직 소속은 `/v1/me` 로 뒤따라 채운다.
  useEffect(() => {
    let disposed = false;

    function handleStorage(event: StorageEvent) {
      if (!isAuthSessionStorageEvent(event)) {
        return;
      }

      const nextSession = loadPersistedAuthSession();
      if (nextSession === null) {
        clearJWT();
        closeSessionWindows();
        dispatch({ type: "auth/sessionEnded" });
        return;
      }

      // verifyAuthSession 이 JWT 를 동기로 설치하므로 optimistic 전이 뒤에 나가는 요청도 토큰을 단다.
      const verification = verifyAuthSession(nextSession);
      dispatch({ type: "auth/sessionStarted", session: nextSession, membership: null });
      verification
        .then((resolution) => {
          if (disposed) {
            return;
          }
          const auth = resolveRestoredAuthState(nextSession, resolution);
          if (auth.status === "signedIn") {
            dispatch({
              type: "auth/sessionStarted",
              session: auth.session,
              membership: auth.membership,
            });
          } else {
            closeSessionWindows();
            dispatch({ type: "auth/sessionEnded" });
          }
        })
        .catch((error: unknown) => {
          logger.error("Failed to verify the session from another tab.", toErrorLogContext(error));
        });
    }

    window.addEventListener("storage", handleStorage);
    return () => {
      disposed = true;
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  // 백엔드가 토큰을 거부하면(apiClient 가 메모리 JWT 를 이미 비웠다) 저장된 세션을 비우고
  // React 상태도 로그아웃 처리한다.
  useEffect(() => {
    return onSessionInvalidated(() => {
      clearPersistedAuthSession();
      closeSessionWindows();
      dispatch({ type: "auth/sessionEnded" });
    });
  }, []);

  const startSession = useCallback(async (session: AuthSession): Promise<MembershipResolution> => {
    const resolution = await verifyAuthSession(session);
    if (resolution.kind !== "member") {
      // 미확인(BE 불통)도 로그인 시점에는 실패다 — 확인 없이 저장하면 새로고침·다른 탭이 그 세션을 믿는다.
      clearJWT();
      return resolution;
    }

    persistAuthSession(session);
    dispatch({ type: "auth/sessionStarted", session, membership: resolution.membership });
    return resolution;
  }, []);

  const endSession = useCallback(() => {
    clearJWT();
    clearPersistedAuthSession();
    closeSessionWindows();
    dispatch({ type: "auth/sessionEnded" });
  }, []);

  const value = useMemo<AppStateContextValue>(
    () => ({ state, startSession, endSession }),
    [state, startSession, endSession],
  );

  return (
    <AppStateContext.Provider value={value}>
      {isInitialized ? children : splash}
    </AppStateContext.Provider>
  );
}

export function useAppState(): AppStateContextValue {
  const context = useContext(AppStateContext);
  if (context === null) {
    throw new Error("useAppState must be used inside <AppStateProvider>.");
  }
  return context;
}
